// Multi-zone trips (B13): a stage may carry its own time zone ("Japan then Korea"), and the trip's
// clock follows the stage under way. Everything that asks "what day and time is it for the trip"
// (the trip clock, reveals, the play-day, the cron's pushes) reads it here.
import type { Pack, Stage } from './pack/schema'
import { zoned, type ZonedParts } from './time'

/** What the zone helpers read of a trip. */
export type ZoneTrip = Trip
type Trip = { meta: Pick<Pack['meta'], 'timezone' | 'startDate' | 'endDate'>; itinerary: { stages: readonly Pick<Stage, 'startDate' | 'endDate' | 'timezone'>[] } }

/** A stage's zone: its own, else the trip's. */
export const stageZone = (trip: Trip, stage: Pick<Stage, 'timezone'> | undefined): string => stage?.timezone ?? trip.meta.timezone

/**
 * The zone the trip is in at `ms`: the first stage whose dates hold that moment's date in the
 * stage's own zone (on a travel day, the stage you're leaving, as the trip clock counts it); before
 * the trip the first stage's, after it the last's. A trip with no stage zones is always the trip's.
 */
export function tripZone(trip: Trip, ms: number): string {
  const stages = trip.itinerary.stages
  if (!stages.some((s) => s.timezone)) return trip.meta.timezone
  for (const s of stages) {
    const date = zoned(ms, stageZone(trip, s)).date
    if (s.startDate <= date && date <= s.endDate) return stageZone(trip, s)
  }
  const first = stages[0]
  return zoned(ms, stageZone(trip, first)).date < (first?.startDate ?? trip.meta.startDate) ? stageZone(trip, first) : stageZone(trip, stages.at(-1))
}

/** The zone a trip date's plans are written in: the first stage holding that date's, else the trip's. */
export function dateZone(trip: Trip, date: string): string {
  return stageZone(trip, trip.itinerary.stages.find((s) => s.startDate <= date && date <= s.endDate))
}

/**
 * The zone a journey lands in on `date`: the last stage holding that day (on a travel day, the stage
 * it arrives into; dateZone gives the one it leaves).
 */
export function arriveZone(trip: Trip, date: string): string {
  return stageZone(trip, trip.itinerary.stages.filter((s) => s.startDate <= date && date <= s.endDate).at(-1))
}

/** The trip's date and time at `ms`, in the zone it's in then (tripZone). */
export function tripNow(trip: Trip, ms: number): ZonedParts & { tz: string } {
  const tz = tripZone(trip, ms)
  return { ...zoned(ms, tz), tz }
}
